if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        // hetdakraam 2026-08-24: EEN gedelegeerde listener op de lijst, in plaats van Dawn's
        // listener per thumbnail-element.
        //
        // Dawn bond hier in de constructor aan elk `[data-target]` los. Dat werkt zolang die
        // elementen blijven staan - en bij ons doen ze dat niet: onze galerij toont per afwerking
        // een andere media-set, dus bij een variantwissel moet de thumbnailrij vervangen worden.
        // Elke vervanging nam de listeners mee het graf in en liet klikbare-ogende miniaturen
        // achter die niets deden. Een gedelegeerde listener leeft op de lijst zelf en overleeft
        // dat, ook als een latere wijziging de rij opnieuw opbouwt zonder eraan te denken.
        this.elements.thumbnails.addEventListener('click', (event) => {
          const drager = event.target.closest('[data-target]');
          if (!drager || !event.target.closest('button')) return;
          this.setActiveMedia(drager.dataset.target, false);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            // Zelfde reden als de guard verderop: bij een per-afwerking gefilterde set kan de
            // thumbnail ontbreken. Deze tak draait nu alleen met prepend=true en onze eigen
            // aanroepen gebruiken prepend=false, dus hij is vandaag onbereikbaar - maar een
            // onbeschermde .parentElement hier is dezelfde tijdbom, een refactor verderop.
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        // ZOEK OP HET ID VAN DE SLIDE DIE ECHT ACTIEF WERD, niet op het gevraagde (Codex 24-08).
        // Bovenaan valt setActiveMedia bij een onbekend mediaId terug op de EERSTE slide. Bleef
        // je hier op het gevraagde id zoeken, dan werd slide 1 actief terwijl `aria-current` op
        // de oude thumbnail bleef staan: beeld en markering wijzen dan verschillende foto's aan,
        // wat voor een schermlezer een regelrechte tegenspraak is.
        const actiefId = activeMedia.dataset.mediaId || mediaId;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${actiefId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        // hetdakraam 2026-08-24: activeThumbnail MAG null zijn en dat is hier geen storing.
        // setActiveThumbnail vangt dat zelf al af (`!thumbnail` -> return), maar deze regel las
        // .dataset onbeschermd en gooide dan een TypeError. Die vloog omhoog tot MIDDEN in
        // updateMedia() van product-info.js, waardoor alles daarna stil oversloeg - precies het
        // gedrag dat de thumbnailrij op de oude afwerking liet staan na een variantwissel.
        //
        // Waarom het kan gebeuren: onze galerij toont per afwerking een ANDERE media-set (het
        // alt-filter in hdr-variant-media.liquid). Vraagt iets een media-id op dat niet in de
        // huidige set zit, dan bestaat de bijbehorende thumbnail niet. In stock-Dawn komt dat
        // niet voor, want daar staat altijd de volledige set in de DOM; vandaar dat Dawn deze
        // regel onbeschermd laat. Een lege live-region-melding is de juiste uitkomst: er valt
        // geen positie te melden.
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      /**
       * hetdakraam 2026-08-24: markeer de thumbnail die hoort bij de slide die NU actief is.
       *
       * Bestaat omdat product-info.js bij een variantwissel de thumbnailrij vervangt door de
       * server-versie, en die draagt `aria-current` altijd op de eerste. Verandert alleen de
       * VOLGORDE van dezelfde media, dan roept product-info geen setActiveMedia aan en zou de
       * markering blijven hangen op een foto die niet in beeld staat.
       *
       * Leest de waarheid uit de viewer in plaats van uit een meegegeven id: wat zichtbaar is,
       * bepaalt wat gemarkeerd hoort te zijn, niet wat een aanroeper dacht te tonen.
       */
      synchroniseerActieveThumbnail() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;
        const actief =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!actief) return;
        this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${actief.dataset.mediaId}"]`));
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
